// SubtenantsList.tsx
// Компонент для отображения и управления списком субарендаторов. 
// Позволяет создавать, редактировать и удалять субарендаторов, а также привязывать их к сущностям
// Отдельная форма для редактирования квот и занятых слотов
import React, { useState } from 'react';
import { Box, Button, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { GridColDef, type GridRenderCellParams } from '@mui/x-data-grid';
import { subtenantApi as api, User } from "../../../api/subtenantApi";
import { api as plateApi } from "../../../api/plateApi";
import { useFetch } from "../../../hooks/useFetch3";
import { Subtenant } from '../../../api/subtenantApi';
import { usePresetDialogs } from '../../dialog';
import { EntityPath } from '../../../types/plate';
import ActionButtons from '../../ActionButtons2';
import { FormModal } from '../../modal/FormModal';
import SubtenantForm from './SubtenantForm';
import { useTranslation } from 'react-i18next';
import LocalizedGrid from '../../localizedGrid/LocalizedGridCook3';
import i18n from '../../../i18n';
import { useAuthStore } from '../../../store/useAuthStore3';
import { SubtenantExtendedForm } from './SubtenantExtendedForm2';

export const SubtenantsList: React.FC = () => {
    const { t } = useTranslation();
    const { customerId, subtenantId } = useAuthStore();
    const { data, setData, error } = useFetch<Subtenant[]>(api.get, []);
    const { data: entityPaths } = useFetch<EntityPath[]>(plateApi.getEntityPaths, []);
    const { data: userList, } = useFetch<User[]>(
        useAuthStore.getState().getUserList,
        [],
        {subtenantId},
        [subtenantId]
    );
    const { showConfirm, DialogComponent } = usePresetDialogs();
    const [selectedSubtenant, setSelectedSubtenant] = useState<Subtenant | null>(null);    
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isModalExtendedOpen, setIsModalExtendedOpen] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    if (!customerId) {
        return <Typography>{t('customer.loading')}</Typography>;
    }    

    const columns: GridColDef[] = [
        { field: "subtenant_id", headerName: "ID", type: "number", flex: 0.5, width: 50 },
        { field: "name", headerName: t('Subtenant.name'), type: "string", flex: 2, width: 200 },
        { field: "max_parking_slots", headerName: t('Subtenant.max_slots'), type: "number", flex: 1, width: 100 },
        { field: "max_permits", headerName: t('Subtenant.max_permits'), type: "number", flex: 1, width: 100 },
        { field: "comment", headerName: t('Subtenant.comment'), type: "string", flex: 2, width: 150 },
        { 
            field: "users_count", 
            headerName: t('Subtenant.users_count'),
            type: "number", 
            flex: 1, 
            width: 100,
            valueGetter: (value, row) => {
                const users = userList.filter(s => s.subtenant_id === row.subtenant_id);
                return users.length || 0;
            }
        },        
        {
            field: 'actions',
            headerName: t('common.actions'),
            width: 150,
            sortable: false,
            renderCell: (params: GridRenderCellParams<Subtenant>) => {
                return (
                    <ActionButtons actions={[
                        { type: 'edit', action: () => handleOpenModal(params.row) },
                        { type: 'delete', action: () => handleDelete(params.row.subtenant_id) },
                        // { type: 'extended', action: () => handleOpenExtended(params.row), tooltip: 'Квота' },
                    ]} />
                );
            },
        },        
    ];

    const handleUpdate = async (formData: Subtenant) => {
        if (!!selectedSubtenant) {
            await api.put(formData);
            await api.putSubtenantToEntity(formData.subtenant_id, formData.entity_ids);
            setData((prev) => prev.map(sub => sub.subtenant_id === formData.subtenant_id ? formData : sub));
        } else {
            const newData = await api.post(formData);
            await api.putSubtenantToEntity(newData.subtenant_id, formData.entity_ids);
            setData((prev) => [...prev, newData]);
        }
        handleCloseModal();
    };

    const handleDelete = async (subtenant_id: number) => {
        const result = await showConfirm(
            t('common.delete_question', { id: subtenant_id }),
            t('common.delete_confirmation')
        );
        if (!result) return;

        await api.delete(subtenant_id);

        const updatedList = data.filter(sub => subtenant_id !== sub.subtenant_id);
        setData(updatedList);
        console.log(`Subtenant with ID ${subtenant_id} deleted.`);
    };
    
    const handleOpenModal = async (subtenant: Subtenant | null) => {
        if (subtenant) {
            const entityIds = await api.getSubtenantToEntity(subtenant.subtenant_id);
            setSelectedSubtenant({ ...subtenant, entity_ids: entityIds });
        } else {
            setSelectedSubtenant(null);
        }
        setIsModalOpen(true);
    };

    const handleOpenExtended = async (subtenant: Subtenant) => {
        setSelectedSubtenant(subtenant);
        setIsModalExtendedOpen(true);
    };

    const handleCloseModal = () => setIsModalOpen(false);

    const handleCloseModalExtended = () => setIsModalExtendedOpen(false);

    return (
        <Box sx={{ width: '100%', p: isMobile ? 1 : 0 }}>
            <Typography variant="h4" gutterBottom>
                {/* Керування Орендарями */}
                {t('pages.Subtenant_Management')}
            </Typography>
                  
            {DialogComponent}
            
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <Button 
                    variant="contained" 
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenModal(null)}
                >
                    {t('SubtenantsList.Add_New_Subtenant')}
                </Button>
            </Stack>

            {!isMobile && (
                <LocalizedGrid
                    lang={i18n.language as any}
                    gridId='SubtenantsList'
                    rows={data}
                    columns={columns} 
                    getRowId={(row) => row.subtenant_id}
                    sx={{ maxWidth: '1000px' }}
                    hideToolbar={true}
                />
            )}

            <Typography color='error'>{error?.message}</Typography>

            <FormModal open={isModalOpen} onClose={handleCloseModal}>
                <SubtenantForm
                    subtenant={selectedSubtenant}
                    entityPaths={entityPaths}
                    onSave={handleUpdate}
                    onCancel={handleCloseModal}
                />
            </FormModal>

            <FormModal open={isModalExtendedOpen} onClose={handleCloseModalExtended}>
                <SubtenantExtendedForm
                    subtenant={selectedSubtenant}
                    onClose={handleCloseModalExtended}
                />
            </FormModal>
        </Box>
    );
};